import api from './api';

/**
 * @typedef {Object} PaginatedResponse
 * @property {Array} results - Array of results
 * @property {number} total - Total count of items
 * @property {boolean} has_more - Whether more pages are available
 * @property {number} limit - Items per page
 * @property {number} offset - Current offset
 */

/**
 * @typedef {Object} SummaryResponse
 * @property {Array} conversations - Array of conversation summaries
 * @property {number} total - Total count
 * @property {boolean} has_more - Whether more pages are available
 */

const API_PREFIX = '/api/v1';

const conversationService = {
  // Get KPI metrics for the dashboard (date-only + tz aware)
  getMetrics: async ({ date_from, date_to, window_hours, tz }) => {
    const params = {};
    if (date_from) params.date_from = date_from; // YYYY-MM-DD
    if (date_to) params.date_to = date_to;       // YYYY-MM-DD
    if (window_hours) params.window_hours = window_hours;
    if (tz) params.tz = tz;                      // IANA timezone
    const response = await api.get(`${API_PREFIX}/conversations/analytics/metrics`, { 
      params,
      headers: tz ? { 'X-User-Timezone': tz } : {}
    });
    return response.data;
  },

  // Get timeseries for the dashboard with bucket & tz
  getTimeseries: async ({ date_from, date_to, bucket = '30m', tz = 'America/Argentina/Buenos_Aires', window_hours = 24 }) => {
    const params = { date_from, date_to, bucket, tz, window_hours };
    const response = await api.get(`${API_PREFIX}/conversations/analytics/timeseries`, { params });
    return response.data;
  },

  // Get top 10 countries for the dashboard
  getTopCountries: async ({ date_from, date_to }) => {
    const params = { date_from, date_to };
    // NOTE: The backend will return placeholder data for this endpoint for now.
    const response = await api.get(`${API_PREFIX}/conversations/analytics/top-countries`, { params });
    return response.data;
  },

  // Get activity heatmap by day of week and hour
  getActivityHeatmap: async ({ date_from, date_to, tz = 'America/Argentina/Buenos_Aires' }) => {
    const params = { date_from, date_to, tz };
    const response = await api.get(`${API_PREFIX}/conversations/analytics/activity-heatmap`, { params });
    return response.data;
  },

  // --- Legacy or Unused Functions (to be refactored or removed) ---

  // Get a summary of conversations (paginated)
  getSummary: async (params) => {
    // Map frontend param names to backend param names
    const backendParams = {
      limit: params.limit,
      offset: params.offset,
      hide_unanswered: params.hideUnanswered || false,
      phone_filter: params.phoneFilter,
      before_timestamp: params.beforeTimestamp,
      before_session_id: params.beforeSessionId
    };
    const response = await api.get(`${API_PREFIX}/conversations/summary`, { params: backendParams });
    return response.data;
  },

  // Get conversations grouped by phone (WhatsApp-style)
  getSummaryGrouped: async (params) => {
    const backendParams = {
      limit: params.limit,
      offset: params.offset,
      hide_unanswered: params.hideUnanswered || false,
      phone_filter: params.phoneFilter,
      before_timestamp: params.beforeTimestamp,
      before_phone: params.beforePhone
    };
    const response = await api.get(`${API_PREFIX}/conversations/summary/grouped`, { params: backendParams });
    return response.data;
  },

  // Get detailed conversation
  getDetail: async (sessionId) => {
    const response = await api.get(`${API_PREFIX}/conversations/detail/${sessionId}`);
    return response.data;
  },

  // Get all messages for a phone number (WhatsApp-style)
  getMessagesByPhone: async (phoneNumber, params = {}) => {
    const encoded = encodeURIComponent(phoneNumber);
    const response = await api.get(`${API_PREFIX}/conversations/by-phone/${encoded}`, { params });
    return response.data;
  },

  // Search conversations
  search: async (filters) => {
    const response = await api.post(`${API_PREFIX}/conversations/search`, filters);
    return response.data;
  },

  // Search conversations grouped by phone (WhatsApp-style)
  searchGrouped: async (filters) => {
    const response = await api.post(`${API_PREFIX}/conversations/search/grouped`, filters);
    return response.data;
  },

  // Get unique phone numbers
  getUniquePhones: async () => {
    const response = await api.get(`${API_PREFIX}/conversations/unique-phones`);
    return response.data;
  },

  // Check for new messages since a given timestamp
  checkForUpdates: async (lastTimestamp) => {
    const response = await api.get(`${API_PREFIX}/conversations/updates`, {
      params: { since: lastTimestamp }
    });
    return response.data;
  },

  // Get new messages in a session since a timestamp
  getNewMessages: async (sessionId, lastTimestamp) => {
    const response = await api.get(`${API_PREFIX}/conversations/new-messages/${sessionId}`, {
      params: { since: lastTimestamp }
    });
    return response.data;
  },

  // Get participant details for the sider
  getParticipantDetails: async (phoneNumber) => {
    const encoded = encodeURIComponent(phoneNumber);
    const response = await api.get(`${API_PREFIX}/conversations/participant-details/${encoded}`);
    return response.data;
  }
};

export default conversationService;
